import React, { useEffect, useRef, useState } from 'react';
import cn from '@/utils/cn';
import SPAButton from '../shared/SPAButton';
import SPAPill from '../shared/SPAPill';
import type { SPAPillTone } from '../shared/SPAPill';
import { REASON_LABELS } from './syncStates';
import useVerify, { GIVE_UP_MS } from './useVerify';
import type { Sent, VerifyPayload } from './useVerify';

type Refusal = { response?: { data?: { errors?: string[] } } };

const Check = ({ children, ok }: { children: React.ReactNode; ok: boolean | null }) => (
  <li className="tw-flex tw-items-baseline tw-gap-2 tw-text-sm">
    <span
      aria-label={ok === false ? 'Problem' : 'OK'}
      className={cn('tw-font-bold', ok === false ? 'tw-text-spa-error' : 'tw-text-spa-success')}
    >
      {ok === false ? '✕' : '✓'}
    </span>
    <span className={ok === false ? 'tw-text-spa-primary' : 'tw-text-spa-secondary'}>{children}</span>
  </li>
);

const LinkChecks = ({ link }: { link: VerifyPayload['link'] }) => (
  <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-1 tw-p-0">
    <Check ok={link.linked}>
      {link.linked ? 'Linked to a gradebook column' : 'Not linked to a gradebook column — nothing can be sent'}
    </Check>
    <Check ok={link.resource_link_id}>
      {link.resource_link_id ? 'Launches from the LMS open this assignment' : 'No launch link — the LMS cannot open it'}
    </Check>
    {link.columns.map(column => (
      <Check key={column.line_item} ok={column.agrees}>
        {column.agrees === false
          ? `The LMS says the column in course ${column.course_id} belongs to assignment ${column.lms_says}`
          : `The LMS confirms the column in course ${column.course_id} is this assignment's`}
      </Check>
    ))}
  </ul>
);

const Count = ({ label, tone, value }: { label: string; tone: SPAPillTone; value: number }) => (
  <SPAPill tone={value === 0 ? 'neutral' : tone}>{`${label} · ${value}`}</SPAPill>
);

// AIDEV-NOTE: The teacher's gradebook colours, so support and the teacher read one answer: green when
// the LMS accepted the score, red when it refused, amber while waiting on the worker.
const Delivery = ({ delivery, sent }: { delivery: NonNullable<VerifyPayload['delivery']>; sent: Sent }) => {
  const reasons = delivery.students.filter(row => row.outcome === 'failed')
    .reduce<Record<string, number>>((acc, row) => {
    // Unrecognised failures are told apart by what the LMS said, since the reason alone says nothing.
    const reason = row.reason ?? 'unknown';
    const key = reason === 'unknown' && row.detail != null ? `unknown — ${row.detail}` : reason;

    return { ...acc, [key]: (acc[key] ?? 0) + 1 };
  }, {});
  const zerosPosted = delivery.students.filter(row => row.outcome === 'delivered' && Number(row.score) === 0).length;
  const gaveUp = delivery.waiting > 0 && Date.now() - new Date(sent.sent_at).getTime() >= GIVE_UP_MS;

  return (
    <div className="tw-mt-3">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
        <Count label="In the LMS" tone="success" value={delivery.delivered} />
        <Count label="Waiting" tone="warning" value={delivery.waiting} />
        <Count label="Failed" tone="error" value={delivery.failed} />
      </div>
      {Object.entries(reasons).map(([reason, count]) => (
        <p key={reason} className="tw-m-0 tw-mt-1.5 tw-text-sm tw-text-spa-error">
          {`${count} failed: ${REASON_LABELS[reason] ?? reason.replace(/^unknown — /, `${REASON_LABELS.unknown} — `)}`}
        </p>
      ))}
      {delivery.waiting > 0 && !gaveUp && (
        <p className="tw-m-0 tw-mt-1.5 tw-text-sm tw-text-spa-secondary">
          Checking every few seconds — each grade takes about a second to send.
        </p>
      )}
      {gaveUp && (
        <p className="tw-m-0 tw-mt-1.5 tw-text-sm tw-text-spa-warning">
          {`${delivery.waiting} never got a response after three minutes. The worker skips a student with `}
          nothing to send (no submission yet, for example), or the queue is backed up — check Sidekiq.
        </p>
      )}
      {delivery.waiting === 0 && delivery.failed === 0 && (
        <p className="tw-m-0 tw-mt-1.5 tw-text-sm tw-text-spa-success">
          {`All ${delivery.delivered} grades are in the LMS.`}
        </p>
      )}
      {zerosPosted > 0 && (
        <p className="tw-m-0 tw-mt-1.5 tw-text-sm tw-text-spa-warning">
          {`${zerosPosted} of those ${zerosPosted === 1 ? 'was' : 'were'} posted as 0 — TCI scores it that way `}
          until the student submits, or when an autograded assignment is past due with no work.
        </p>
      )}
    </div>
  );
};

interface VerifyPanelProps {
  assignmentId: string;
  /** Opened by a repair: scrolled into view so the check is the next thing support sees. */
  focus: boolean;
}

const VerifyPanel = ({ assignmentId, focus }: VerifyPanelProps) => {
  const [sent, setSent] = useState<Sent | null>(null);
  const { redrive, verify } = useVerify(assignmentId, sent);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    if (focus) ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [focus]);

  const { data } = verify;
  // Both come back null while a re-send is being polled; neither is shown then.
  const toSend = data?.to_send ?? 0;
  const zeros = data?.zeros ?? 0;
  const refusal = (redrive.error as Refusal)?.response?.data?.errors?.[0];
  const send = () => redrive.mutate(undefined, { onSuccess: result => setSent(result) });

  return (
    <section
      ref={ref}
      className={cn(
        'tw-mb-6 tw-rounded tw-border tw-border-solid tw-bg-spa-bg-secondary tw-px-4 tw-py-3',
        focus ? 'tw-border-spa-accent' : 'tw-border-spa-border',
      )}
    >
      <div className="tw-flex tw-flex-wrap tw-items-baseline tw-justify-between tw-gap-3">
        <h3 className="tw-m-0 tw-text-base tw-font-semibold tw-text-spa-heading">Check the link and send grades</h3>
        {data != null && sent == null && toSend > 0 && (
          <SPAButton
            disabled={!data.link.linked || redrive.isLoading}
            size="xs"
            type="button"
            variant="primary"
            onClick={send}
          >
            {toSend === 1 ? 'Send 1 missing grade' : `Send ${toSend} missing grades`}
          </SPAButton>
        )}
      </div>

      {verify.isLoading && <p className="tw-m-0 tw-mt-2 tw-text-sm tw-text-spa-secondary">Checking with the LMS…</p>}
      {data != null && (
        <div className="tw-mt-2">
          <LinkChecks link={data.link} />
          {sent == null && data.link.linked && (
            <p className="tw-m-0 tw-mt-2 tw-text-sm tw-text-spa-secondary">
              {toSend === 0
                ? 'Every student with a score has it in the LMS — nothing to send.'
                : `${toSend} ${toSend === 1 ? 'student has' : 'students have'} a score `
                  + 'that is not in the LMS. Sending re-posts only those; students with no work are never sent a zero.'}
            </p>
          )}
          {sent != null && data.delivery != null && <Delivery delivery={data.delivery} sent={sent} />}
          {zeros > 0 && (
            <p className="tw-m-0 tw-mt-2 tw-text-sm tw-text-spa-secondary">
              {`${zeros} ${zeros === 1 ? 'student' : 'students'} with no submitted work `}
              get a 0 from the normal sync (past due). Those are not sent from here.
            </p>
          )}
        </div>
      )}
      {refusal != null && <p className="tw-m-0 tw-mt-2 tw-text-sm tw-text-spa-error">{refusal}</p>}
    </section>
  );
};

export default VerifyPanel;
